import { CustomTableCell, CustomTableHead, Dialog, FormFileInput, FormInput, FormSectionHeader, FormSelect, Loader } from "@/components";
import useStep5 from "./useStep5";
import { Badge, Button, Card, CardContent, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, Label, RadioGroup, RadioGroupItem, Table, TableBody, TableHeader, TableRow } from "@/components/ui";
import { Dot, EyeIcon, House, MapPin, MoreVertical, Network, Pen, Star, Trash2, Users } from "lucide-react";
import { ADDRESS_1, ADDRESS_2, CITY, COMMUNITY, CORRESPONDENCE_ADDRESS, CUSTOM_ADDRESS, EMAIL_ID_1, EXISTING, FIRST_NAME, JOINT, LAST_NAME, MOBILE_NO, OCCUPANCY_TYPE, ORGANIZATION_NAME, OWNERSHIP_SHARE, OWNERSHIP_TYPE, PO_BOX, PRIMARY_USER, PROPERTY, STATE, USERS, USER_TYPE, ZIPCODE, addressCorrespondOptions, orgOwnershipType, ownershipTypeOptions} from "./consts";
import { capitalize, cn } from "@/lib/utils";
import { AddEditDialog } from "./AddEditDialog";
import { RemoveJointOwnerDialog } from "./RemoveJointOwnerDialog";
import { FormProvider } from "react-hook-form";
import { StepsProps } from "../types";
import { FILE_SIZE, FILE_TYPE, IS_REQUIRED, PROOF_DOCS, PROOF_FILE, PROOF_TYPE, UPLOADED_STATUS, uploadStatusStyle } from "../../consts";

const Step5 = ({
  onCancel,
  onSubmit: onSubmitBaseForm,
  isView: isBaseView,
  isAddPending
}: StepsProps) => {
  const { form, isCommunityLoading, communityList, onSubmit,
    propertyList, onOwnershipTypeChange, formData, ownerCount,
    onAddUser, onSave, isMobile, onEditUser, selectedUser, action, onChangePrimary, isView,
    openAddEditDialog, setOpenAddEditDialog, onAddressTypeChange, openRemoveOwnerDialog, setOpenRemoveOwnerDialog, onRemoveOwner,
    onRemoveSave, isStateLoading, stateList, keys, isOwnershipDtlLoading, proofDocs, fileInputRefs, onRemoveProof,
    onAddMore, onDeleteConfirmIndividualUser, onDeleteIndividualUser, openDeleteSingleOwnerDialog, setOpenDeleteSingleOwnerDialog,
    isDocListLoading, updatedKey, ocpDocList, onViewAttachment, isUpdateDocPending
   } = useStep5({onSubmitBaseForm});
   const { errors } = form.formState;

  return (
    <div className="p-4 md:p-6">
      {isCommunityLoading || isStateLoading || isOwnershipDtlLoading ? <Loader /> :
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="grid grid-cols-1 mt-4 gap-[20px]">
              <Card>
                <CardContent>
                  <FormSectionHeader className="flex flex-row items-center gap-2">
                    <div className="flex items-center justify-center h-8 w-8 bg-primary rounded-full">
                      <Network className="stroke-white h-4 w-4" />
                    </div>
                    Attach To Property
                  </FormSectionHeader>

                  <div className="grid gird-cols-1 md:grid-cols-2 gap-[20px]">
                    <FormSelect
                      name={COMMUNITY}
                      label="Community"
                      placeholder="Please Select"
                      options={communityList?.map((d: any) => ({
                        value: d.communityId,
                        label: d.name,
                      }))}
                      disabled
                    />
                    <FormSelect
                      name={PROPERTY}
                      label="Property"
                      placeholder="Please Select"
                      options={propertyList?.map((d: any) => ({
                        value: d.propertyId?.toString(),
                        label: d.name,
                      }))}
                      disabled
                    />
                  </div>
                </CardContent>
              </Card>

              {formData[PROPERTY] ? <>
                <Card>
                  <CardContent>
                    <FormSectionHeader className="flex flex-row items-center gap-2">
                      <div className="flex items-center justify-center h-8 w-8 bg-primary rounded-full">
                        <House className="stroke-white h-4 w-4" />
                      </div>
                      <div className="flex flex-col">
                        <span>Ownership Type</span>
                        <span className="text-gray-500 text-[12px] font-normal">Defines the title on this property; drives correspondence & how many owners are required.</span>
                      </div>
                    </FormSectionHeader>

                    <div className="flex flex-col md:flex-row gap-3">
                      {ownershipTypeOptions?.map((data: any) => {
                        const Icon = data.icon;
                        return (
                          <Button variant="outline" type="button"
                            className={cn(formData[OWNERSHIP_TYPE] == data.value ? "text-white bg-primary hover:text-white hover:bg-primary" : "text-gray-500")}
                            onClick={onOwnershipTypeChange(data.value)} disabled={isView || isBaseView}>
                            <Icon className="h-5 w-5" />
                            {data?.label}
                          </Button>
                        )
                      })}
                    </div>

                    <div className="bg-blue-50 rounded-sm flex flex-row items-center gap-3 text-primary text-[12px] p-3 mt-4">
                      <div className="h-3 w-3 bg-primary rounded-full" />
                      <span>Individual title allows <span className="font-semibold">one Owner only.</span> Additional people can be added as tenants or other type.</span>
                    </div>
                  </CardContent>
                </Card>

                {orgOwnershipType.includes((formData as any)[OWNERSHIP_TYPE]) ? <Card>
                  <CardContent className="grid grid-cols-1 md:grid-cols-2">
                    <FormInput
                      name={`${ORGANIZATION_NAME}`}
                      control={form.control}
                      label="Organization Name"
                      placeholder="Please enter"
                      error={(errors as any)[`${ORGANIZATION_NAME}`]?.message}
                      isRequired
                    />
                  </CardContent>
                </Card> : <></>}

                <Card>
                  <CardContent>
                    <FormSectionHeader className="flex flex-col md:flex-row md:items-center gap-2 justify-between w-full">
                      <div className="flex flex-row items-center gap-2">
                        <div className="flex items-center justify-center h-8 w-8 bg-primary rounded-full">
                          <Users className="stroke-white h-4 w-4" />
                        </div>
                        <div className="flex flex-col">
                          <span>Users on this title <span className="text-gray-400 text-[12px]">({ownerCount} Owner)</span></span>
                        </div>
                      </div>
                      <Button type="button" onClick={onAddUser} disabled={isView || isBaseView}>+ Add User</Button>
                    </FormSectionHeader>

                    {(formData as any)[USERS]?.length ?
                      <div className="flex flex-col gap-3">
                        {(formData as any)[USERS]?.map((data: any, idx: number) => {
                          if (data.delete) return;
                          const isOwner = data[OCCUPANCY_TYPE] == "OWNER";
                          return (
                            <div className="grid grid-cols-1 md:grid-cols-6 border border-gray-300 px-2 py-3 rounded-sm">
                              <div className="flex flex-row gap-2 items-center md:col-span-4">
                                {!isMobile && <div className="bg-gray-300 flex items-center justify-center h-[25px] w-[25px] rounded-sm mr-3">{idx+1}</div>}
                                <div className="bg-primary flex items-center justify-center rounded-full h-[30px] w-[30px] md:h-[50px] md:w-[50px]">
                                  <span className="text-white text-[15px]">{data[FIRST_NAME]?.charAt(0)?.toUpperCase()}{data[LAST_NAME]?.charAt(0)?.toUpperCase()}</span>
                                </div>
                                <div className="flex flex-col">
                                  <div className="flex flex-col md:flex-row md:items-end gap-1">
                                    <span className="font-medium">{data[FIRST_NAME]} {data[LAST_NAME]}</span>
                                    <div className="flex flex-row items-center gap-1">
                                      <span className="text-[10px] bg-blue-100 text-primary px-2 py-1 rounded-sm font-medium">{data[USER_TYPE] == EXISTING ? "Existing User" : "New User"}</span>
                                      {data[PRIMARY_USER] ? <span className="text-[10px] bg-green-100 text-green-700 px-2 py-1 rounded-sm font-medium">Primary</span> : <></>}
                                    </div>
                                  </div>
                                  <div className="text-gray-400 text-[12px] flex flex-col md:flex-row md:items-center">
                                    <span>{data[EMAIL_ID_1]}</span>
                                    {!isMobile && <Dot className="h-3 w-3"/>}
                                    <span>{data[MOBILE_NO]}</span>
                                  </div>
                                </div>
                              </div>
                              <div className="flex flex-row gap-2 items-center md:justify-end mt-2 md:mt-auto self-end md:col-span-2">
                                <div className="flex flex-row bg-gray-100 gap-2 items-center p-2 rounded-sm">
                                  <Label className="text-[10px] text-gray-400">Type: </Label>
                                  <span className="text-[12px] ">{capitalize(data[OCCUPANCY_TYPE])}</span>
                                </div>
                                {isOwner ? <div className="flex flex-row bg-gray-100 gap-2 items-center p-2 rounded-sm">
                                  <Label className="text-[10px] text-gray-400">Share(%): </Label>
                                  <span className="text-[12px] ">{data[OWNERSHIP_SHARE] || 0}</span>
                                </div> : <></>}
                                {isView ? <></> : <DropdownMenu>
                                  <DropdownMenuTrigger>
                                    <MoreVertical className="w-5 h-7 cursor-pointer" />
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent
                                    align="end"
                                    className="bg-white border-0 shadow-md"
                                  >
                                    <DropdownMenuItem
                                      className="flex items-center gap-2"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onEditUser(data, idx);
                                      }}
                                    >
                                      <Pen className="w-4 h-4" />
                                      Edit
                                    </DropdownMenuItem>
                                    {isOwner ? <DropdownMenuItem
                                      className="flex items-center gap-2"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onChangePrimary(idx);
                                      }}
                                    >
                                      <Star className={cn("w-4 h-4", data[PRIMARY_USER] ? "fill-primary" : "")} />
                                      Make Primary Contact
                                    </DropdownMenuItem> : <></>}
                                    {(formData as any)[OWNERSHIP_TYPE] == JOINT ? <DropdownMenuItem
                                      className="flex items-center gap-2"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onRemoveOwner(data);
                                      }}
                                    >
                                      <Trash2 className={cn("w-4 h-4")} />
                                      Delete
                                    </DropdownMenuItem> : <DropdownMenuItem
                                      className="flex items-center gap-2"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onDeleteIndividualUser(data);
                                      }}
                                    >
                                      <Trash2 className={cn("w-4 h-4")} />
                                      Delete
                                    </DropdownMenuItem>}
                                  </DropdownMenuContent>
                                </DropdownMenu>}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    : <div className="border border-gray-300 rounded-sm flex items-center justify-center flex flex-col py-5 gap-5">
                      <span className="text-[16px] text-center font-semibold">No user are linked to this property yet.</span>
                      <span className="text-[14px] text-gray-400 text-center">Add an existing owner from the user Master, <br/>
                      or create a new owner and link them to this property.</span>
                    </div>}
                  </CardContent>
                </Card>

                <Card>
                  <CardContent>
                    <FormSectionHeader className="text-[15px] font-semibold text-g-700 flex flex-col">
                      <span>Proof Validation</span>
                      <span className="text-[12px] text-gray-400 font-normal">Proof types are populated from Master Data.</span>
                    </FormSectionHeader>
                    <div className="mt-5 flex flex-col gap-5 rounded-md border border-gray-200 p-5">
                    {isDocListLoading ? <Loader /> : 
                      <FormProvider {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)}>
                          <div className="overflow-x-auto w-full mt-5">
                            <div className="min-w-[1000px]">
                              <Table>
                                <TableHeader>
                                  <TableRow className="mb-3 rounded">
                                    <CustomTableHead className="w-[150px]">
                                      Proof Type
                                    </CustomTableHead>
                                    <CustomTableHead className="w-[150px]">
                                      Proof File
                                    </CustomTableHead>
                                    <CustomTableHead className="w-[150px]">
                                      Size
                                    </CustomTableHead>
                                    <CustomTableHead className="w-[150px]">
                                      Status
                                    </CustomTableHead>
                                    <CustomTableHead className="w-[150px]">
                                      Action
                                    </CustomTableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {proofDocs?.length ? proofDocs?.map((_: any, index: number) => {
                                    const file = formData?.[PROOF_DOCS]?.[index]?.[PROOF_FILE];
                                    const isUploaded = formData?.[PROOF_DOCS]?.[index]?.[UPLOADED_STATUS] && !(formData?.[PROOF_DOCS]?.[index]?.[PROOF_FILE] instanceof File);
                                    return (
                                      <TableRow
                                        key={`row-${index}-${_?.[PROOF_TYPE]}`}
                                        onClick={(e) => {
                                          // e.preventDefault();
                                          e.stopPropagation();
                                        }}
                                      >
                                        <CustomTableCell>
                                          <div className="flex flex-row items-center gap-3">
                                            <FormSelect
                                              name={`${PROOF_DOCS}.${index}.${PROOF_TYPE}`}
                                              control={form.control}
                                              label=""
                                              options={ocpDocList?.map((data: any) => ({
                                                label: data?.documentName,
                                                value: data?.documentTypeId?.toString(),
                                                disabled: data?.requirementType == "REQUIRED",
                                              }))}
                                              placeholder="Please select"
                                              error={
                                                (errors as any)?.[PROOF_DOCS]?.[index]?.[PROOF_TYPE]?.message as string
                                              }
                                              isRequired={formData?.[PROOF_DOCS]?.[index]?.[IS_REQUIRED]}
                                              disabled={isView || formData[PROOF_DOCS][index]?.[IS_REQUIRED]}
                                            />
                                            {formData[PROOF_DOCS][index]?.[IS_REQUIRED] ? <Badge
                                              variant="outline"
                                              className={cn(
                                                "bg-amber-50 border-amber-600 border text-amber-600",
                                                "rounded-full px-2 py-1"
                                              )}
                                            >
                                              {"Required"}
                                            </Badge> : <Badge
                                              variant="outline"
                                              className={cn(
                                                "bg-gray-50 border-black border text-black",
                                                "rounded-full px-2 py-1"
                                              )}
                                            >
                                              {"Optional"}
                                            </Badge>}
                                          </div>
                                        </CustomTableCell>
                                        <CustomTableCell>
                                          <FormFileInput
                                            key={updatedKey}
                                            fileInputRef={fileInputRefs.current[index]}
                                            name={`${PROOF_DOCS}.${index}.${PROOF_FILE}`}
                                            control={form.control}
                                            label=""
                                            placeholder="Upload File"
                                            errors={
                                              (errors as any)?.[PROOF_DOCS]?.[index]?.[PROOF_FILE]?.message as string
                                            }
                                            isRequired={formData[PROOF_DOCS][index]?.[IS_REQUIRED]}
                                            disabled={isView}
                                            accept={formData[PROOF_DOCS][index]?.[FILE_TYPE]}
                                            maxSize={formData[PROOF_DOCS][index]?.[FILE_SIZE]}
                                          />
                                        </CustomTableCell>
                                        <CustomTableCell>
                                          <div className="flex items-center justify-center">{formData?.[PROOF_DOCS]?.[index]?.[PROOF_FILE]?.size ? `${parseFloat((formData?.[PROOF_DOCS]?.[index]?.[PROOF_FILE]?.size / (1024 * 1024)).toString()).toFixed(2)} MB` : "-"} </div>
                                        </CustomTableCell>
                                        <CustomTableCell>
                                          {!!file ? <div className="flex items-center justify-center">
                                            <Badge
                                              variant="outline"
                                              className={cn(
                                                uploadStatusStyle[isUploaded ? "uploaded" : "pending"],
                                                "rounded-full px-4 py-2"
                                              )}
                                            >
                                              {isUploaded ? "Uploaded" : "Pending"}
                                            </Badge>
                                          </div> : <></>}
                                        </CustomTableCell>
                                        <CustomTableCell>
                                          <div className="flex items-center justify-center">
                                            {!!file ? (
                                              <Button
                                                variant="link"
                                                type="button"
                                                onClick={() => onViewAttachment(
                                                  file?.url ? file?.url : 
                                                  URL.createObjectURL(file)
                                                )}
                                              >
                                                <EyeIcon className="h-5 w-5" /> View
                                              </Button>
                                            ) : <></>}
                                            {!formData[PROOF_DOCS][index]?.[IS_REQUIRED] && (
                                              <div className="flex items-center">
                                                <Button
                                                  type="button"
                                                  variant="ghost"
                                                  onClick={onRemoveProof(index)}
                                                  disabled={isView}
                                                >
                                                  <Trash2 className="h-6 w-6 stroke-red-600" />
                                                </Button>
                                              </div>
                                            )}
                                          </div>
                                        </CustomTableCell>
                                      </TableRow>
                                    );
                                  }) : <></>}
                                </TableBody>
                              </Table>
                            </div>
                          </div>
                        </form>
                      </FormProvider>
                    }
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={onAddMore}
                      disabled={isView}
                      className="mt-4 text-primary hover:bg-transparent hover:text-primary"
                    >
                      + Add More
                    </Button>
                  </CardContent>
                </Card>

                <Card className="shadow-none rounded-sm p-0 border-gray-300">
                  <CardContent className="p-4">
                    <FormSectionHeader className="flex flex-row items-center gap-2 justify-between w-full">
                      <div className="flex flex-row items-center gap-2">
                        {/* <div className="flex items-center justify-center h-8 w-8 bg-primary rounded-full">
                          <MapPin className="stroke-white h-4 w-4" />
                        </div> */}
                        <div className="flex flex-col">
                          <span>Address For Correspondence</span>
                        </div>
                      </div>
                    </FormSectionHeader>

                    <div className="bg-blue-50 rounded-sm flex flex-row items-center gap-3 text-primary text-[12px] p-3 mt-4">
                      <div className="h-[15px] w-[20px] bg-primary rounded-full" />
                      <span>Editable by the Primary Contact only - every change to the correspondence address is written to the audit trail (who/ when / old - new).</span>
                    </div>

                    <span className="text-[12px] text-gray-500">Choose one source (single point of truth — the app maps to the live record, so there's no copy drift):</span>

                    <div className="my-4">
                      <RadioGroup
                        onValueChange={onAddressTypeChange}
                        value={(formData as any)[CORRESPONDENCE_ADDRESS]}
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
                        disabled={isView || isBaseView}
                      >
                        {addressCorrespondOptions?.map((data: any, index: number) => {
                          const isSelected = (formData as any)[CORRESPONDENCE_ADDRESS] == data?.value;
                          return (
                            <div className={cn("border-2 border-gray-300 rounded-sm p-4", isSelected ? "border-primary" : "")}>
                              <div className="flex flex-row items-start gap-2">
                                <RadioGroupItem
                                  value={data?.value}
                                  id={data?.value}
                                  className={cn(isSelected ? "border-primary" : "", "cursor-pointer")}
                                />
                                <Label htmlFor={data?.value} className="font-medium text-[16px] leading-5 cursor-pointer">{index+1}. {data?.title}</Label>
                              </div>
                              <Label className="my-4 font-thin text-[12px]">{data?.text1}</Label>
                              <Label className="text-green-600 font-thin text-[12px]">{data?.text2}</Label>
                            </div>
                          )
                        })}
                      </RadioGroup>
                    </div>

                    {/* <div>
                      <FormCheckbox
                        name={`${MULTI_CORRESPOND}`}
                        control={form.control}
                        label={<span className="text-black text-[12px]"><span className="font-semibold">Multi-correspondence</span> — email → all opted-in owners; physical mail lists all names.</span>}
                        error={(errors as any)[`${MULTI_CORRESPOND}`]?.message}
                        isRequired={requiredFields.includes(`${MULTI_CORRESPOND}`)}
                        disabled={isView || isBaseView}
                      />
                    </div> */}
                  </CardContent>
                </Card>
                {formData[CORRESPONDENCE_ADDRESS] == "custom" ?
                  <Card className="shadow-none rounded-sm p-0 border-gray-300">
                    <CardContent className="p-4">
                      <FormSectionHeader className="flex flex-row items-center gap-2 justify-between w-full">
                        <div className="flex flex-row items-center gap-2">
                          <div className="flex items-center justify-center h-8 w-8 bg-primary rounded-full">
                            <MapPin className="stroke-white h-4 w-4" />
                          </div>
                          <div className="flex flex-col">
                            <span>Custom Address</span>
                          </div>
                        </div>
                      </FormSectionHeader>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[30px]">
                        <FormInput
                          name={`${CUSTOM_ADDRESS}.${ADDRESS_1}`}
                          control={form.control}
                          label="Address 1"
                          placeholder="Please enter"
                          error={(errors as any)[`${CUSTOM_ADDRESS}`]?.[`${ADDRESS_1}`]?.message}
                          isRequired
                        />
                        <FormInput
                          name={`${CUSTOM_ADDRESS}.${ADDRESS_2}`}
                          control={form.control}
                          label="Address 2"
                          placeholder="Please enter"
                          error={(errors as any)[`${CUSTOM_ADDRESS}`]?.[`${ADDRESS_2}`]?.message}
                        />
                        <FormSelect
                          key={keys.state}
                          name={`${CUSTOM_ADDRESS}.${STATE}`}
                          control={form.control}
                          label="State"
                          placeholder="Please select"
                          error={(errors as any)[`${CUSTOM_ADDRESS}`]?.[`${STATE}`]?.message}
                          isRequired
                          options={stateList?.map((data: any) => ({
                            label: data.name,
                            value: data.code,
                          }))}
                        />
                        <FormInput
                          name={`${CUSTOM_ADDRESS}.${CITY}`}
                          control={form.control}
                          label="City"
                          placeholder="Please select"
                          error={(errors as any)[`${CUSTOM_ADDRESS}`]?.[`${CITY}`]?.message}
                          isRequired
                          variant="char"
                        />
                        <FormInput
                          name={`${CUSTOM_ADDRESS}.${ZIPCODE}`}
                          control={form.control}
                          label="Zipcode"
                          placeholder="Please enter"
                          error={(errors as any)[`${CUSTOM_ADDRESS}`]?.[`${ZIPCODE}`]?.message}
                          isRequired
                          maxLength={10}
                          variant="integerSpeChar"
                        />
                        <FormInput
                          name={`${CUSTOM_ADDRESS}.${PO_BOX}`}
                          control={form.control}
                          label="PO Box"
                          placeholder="Please enter"
                          error={(errors as any)[`${CUSTOM_ADDRESS}`]?.[`${PO_BOX}`]?.message}
                          isRequired
                          minLength={3}
                          maxLength={3}
                          variant="integer"
                        />
                      </div>
                    </CardContent>
                  </Card> 
                : <></>}
              </> : <></>}
            </div>

            <div className="mt-7 flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                className="min-w-[110px]"
                onClick={onCancel}
                disabled={isAddPending || isUpdateDocPending}
              >
                Cancel
              </Button>
              {isView ? <></> :<Button className="min-w-[90px]" disabled={isAddPending || isUpdateDocPending}>
                {isAddPending || isUpdateDocPending && <Loader variant="secondary" />} Save & Next
              </Button>}   
            </div>
          </form>
        </FormProvider>
      }

      {openAddEditDialog && 
        <AddEditDialog
          open={openAddEditDialog}
          setOpen={setOpenAddEditDialog}
          onSave={onSave}
          data={selectedUser}
          isEdit={action == "edit"}
          ownershipType={formData[OWNERSHIP_TYPE] as string}
          ownerCount={ownerCount}
        />
      }

      {
        openRemoveOwnerDialog && 
         <RemoveJointOwnerDialog
          open={openRemoveOwnerDialog}
          setOpen={setOpenRemoveOwnerDialog}
          selectedUser={selectedUser}
          allUsers={formData[USERS]}
          onSave={onRemoveSave}
        />
      }
      {
        openDeleteSingleOwnerDialog && 
        <Dialog
          open={openDeleteSingleOwnerDialog}
          setOpen={setOpenDeleteSingleOwnerDialog}
          title={
            <div className="text-2xl font-bold text-primary">
              {"Confirmation"}
              <div className="h-1 w-[30%] bg-primary rounded-full" />
            </div>
          }
          saveText="Delete"
          saveBtnClassName="bg-red-500 text-white hover:bg-red-500 hover:text-white"
          onSave={onDeleteConfirmIndividualUser}
        >
          <p className="text-sm">
            Are you sure you want to delete the user -{" "}
            <span className="font-bold text-primary">
              {(selectedUser as any)?.[FIRST_NAME]} {(selectedUser as any)?.[LAST_NAME]}
            </span>
            ?
          </p>
        </Dialog>
      }
    </div>
  )
}

export default Step5;
